import { z } from "zod";
import i18n from "@/lib/i18n";
import { normalizeDigits } from "@/lib/digits";
import { isValidStoredPhone } from "@/components/PhoneInput";
import { OTHER_AREA_VALUE } from "@/data/istanbulAreas";

const t = (key: string) => i18n.t(key);

// Latin-only validator kept for legacy code paths that still need it.
const englishOnlyRegex = /^[A-Za-z0-9 .,'\-+/&()]+$/;

export const englishOnly = (max = 100) =>
  z
    .string()
    .trim()
    .min(1, { message: t("validate.required") })
    .max(max)
    .regex(englishOnlyRegex, { message: t("validate.englishOnly") });

// Person/display name — accepts Arabic, Latin, Turkish, common punctuation.
// Rejects: empty, whitespace-only, > max chars.
export const nameSchema = (max = 100) =>
  z
    .string()
    .trim()
    .min(1, { message: t("validate.required") })
    .max(max);

// WhatsApp normalisation/validation. Accepts Arabic digits, +, 00 prefix.
export const normalizeWhatsapp = (raw: string) => {
  const digits = normalizeDigits(raw).replace(/[\s\-()]/g, "").trim();
  if (!digits) return "";
  if (digits.startsWith("+")) return "00" + digits.slice(1);
  if (digits.startsWith("00")) return digits;
  return digits;
};

export const whatsappSchema = () =>
  z
    .string()
    .trim()
    .min(1, { message: t("validate.required") })
    .transform(normalizeWhatsapp)
    .refine((v) => isValidStoredPhone(v), {
      message: t("validate.invalidWhatsApp"),
    });

export const emailSchema = (required = true) => {
  const base = z.string().trim().email({ message: t("validate.invalidEmail") }).max(255);
  return required ? base : base.or(z.literal("")).optional();
};

// Two password schemas — relaxed for end-users, strict for admins.
export const passwordSchemaUser = () =>
  z.string().min(6, { message: t("validate.passwordTooShortUser") }).max(128);

export const passwordSchemaAdmin = () =>
  z.string().min(8, { message: t("validate.passwordTooShortAdmin") }).max(128);

// Backwards compat — defaults to relaxed user policy.
export const passwordSchema = passwordSchemaUser;

// Numeric helpers — accept Arabic digits.
const numericString = () =>
  z
    .string()
    .transform((v) => normalizeDigits(v ?? "").trim())
    .pipe(z.string().min(1, { message: t("validate.required") }));

export const positiveNumber = () =>
  z
    .union([z.string(), z.number()])
    .transform((v) => Number(normalizeDigits(String(v))))
    .refine((n) => !Number.isNaN(n) && n > 0, {
      message: t("validate.priceGtZero"),
    });

export const requiredString = (max = 500) =>
  z.string().trim().min(1, { message: t("validate.required") }).max(max);

// Pre-built schemas
export const apartmentRequestSchema = () =>
  z
    .object({
      full_name: nameSchema(100),
      whatsapp_number: whatsappSchema(),
      email: emailSchema(false),
      apartment_type: z.enum(["studio", "1+1", "2+1", "3+1", "4+1", "5+2", "villa", "duplex", "other"], {
        errorMap: () => ({ message: t("validate.invalidOption") }),
      }),
      apartment_type_note: z.string().trim().max(150).optional(),
      currency: z.enum(["USD", "TRY"], {
        errorMap: () => ({ message: t("validate.invalidOption") }),
      }),
      area: requiredString(120),
      budget_min: z.string().optional(),
      budget_max: z.string().optional(),
      notes: z.string().max(2000).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.apartment_type === "other" && !(data.apartment_type_note ?? "").trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validate.required"),
          path: ["apartment_type_note"],
        });
      }
      if (data.budget_min && data.budget_max && Number(normalizeDigits(data.budget_min)) >= Number(normalizeDigits(data.budget_max))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("validate.minLessThanMax"),
          path: ["budget_min"],
        });
      }
    });

export const submitPropertySchema = () =>
  z.object({
    title: requiredString(150),
    description: z.string().trim().max(3000).optional(),
    area: requiredString(120),
    apartment_type_note: z.string().trim().max(150).optional(),
    bathrooms: numericString(),
    size_sqm: numericString(),
    price: numericString().refine((v) => Number(v) > 0, { message: t("validate.priceGtZero") }),
    google_maps_url: z.string().trim().max(500).optional().or(z.literal("")),
    advertiser_name: nameSchema(100),
    advertiser_whatsapp: whatsappSchema(),
    advertiser_email: emailSchema(false),
  });

export const viewingRequestSchema = () =>
  z.object({
    full_name: nameSchema(100),
    whatsapp_number: whatsappSchema(),
    email: emailSchema(false),
    notes: z.string().max(2000).optional(),
  });

export const authSchema = (mode: "signin" | "signup" | "forgot") => {
  if (mode === "forgot") return z.object({ email: emailSchema(true) });
  return z.object({
    email: emailSchema(true),
    // signup uses relaxed user policy; signin merely checks length to avoid blocking legacy users.
    password: passwordSchemaUser(),
  });
};

// Re-export to make available to UI helpers.
export { OTHER_AREA_VALUE };
